import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import SimplePanel from '@/shared/components/SimplePanel';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import ScrollArrow from '@/shared/components/ScrollArrow';
import handleHttpError from '@/shared/services/HandleHttpError';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import RegistrationsPerReportFilters, {
  defaultFilters,
  getParamsFromFilters,
} from './RegistrationsPerReportFilters';
import ExportToExcelDropdown from './ExportToExcelDropdown';
import messages from '../reportingMessages';
import { getReportTypeInfo } from './helpers';

import RegistrationsPerReportTable from './RegistrationsPerReportTable';

const StoragePrefix = 'RegistrationsPerReportFilters';

const RegistrationsPerReport = ({ reportType, externalParams = {}, intl, reportKey }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [reportInfo] = useState(getReportTypeInfo(reportType));
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState(() => {
    const sessionParams =
      JSON.parse(sessionStorage.getItem(`${StoragePrefix}-${reportType}`)) || {};
    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }
    return {
      ...defaultFilters,
      ...sessionParams,
      ...externalParams,
    };
  });
  const [mainTaskTemplates, setMainTaskTemplates] = useState([]);

  // Retrieve data
  useEffect(() => {
    const cancelToken = Axios.CancelToken.source();
    setLoading(true);

    const languageCode = intl.locale;

    const params = {
      ...getParamsFromFilters(filters),
      take: 20,
      page: 0,
      languageCode,
    };

    const sessionParams = JSON.stringify(filters);
    sessionStorage.setItem(`${StoragePrefix}-${reportType}`, sessionParams);
    api.base
      .post(reportInfo.url, params, {
        cancelToken: cancelToken.token,
      })
      .then(({ data }) => {
        const newRows = data.rows;
        const totalRow = {
          rowDepth: 0,
          rowType: -1,
          rowKey: 'Totals',
          ...(data.totals || {}),
        };
        setRows([totalRow, ...newRows]);
        setTotals(data.totals || {});
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        setRows([]);
        if (!Axios.isCancel(error)) return handleHttpError(intl)(error);
      });
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [filters, intl, reportType, reportInfo]);

  // Retrieve task templates
  useEffect(() => {
    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) =>
      setMainTaskTemplates(
        data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
          }))
          .sort((a, b) => (a.label > b.label) - (a.label < b.label)), // Sort on label property
      ),
    );
  }, [intl]);

  const createExcelReport = useCallback(
    (isDetailsReport = false) => {
      const reportParams = { ...getParamsFromFilters(filters), isDetailsReport };
      const languageCode = intl.locale;

      const params = {
        language: languageCode,
        signalRConnectionId: SignalRService.getConnectionId(),
        report: reportType,
        parameters: JSON.stringify(reportParams),
      };
      setExporting(true);

      return api.base
        .post(`${config.appUrls.reporting}/api/export`, params)
        .then(() => {
          ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        })
        .catch(error => {
          handleHttpError(intl)(error);
        })
        .finally(() => {
          setExporting(false);
        });
    },
    [intl, filters, reportType],
  );

  const updateFilters = newFilters => {
    setFilters({
      ...filters,
      ...newFilters,
    });
  };

  const showDetails = filters.groupBy.indexOf(99) >= 0;
  let [lastGroup] = [...filters.groupBy].reverse();
  if (!lastGroup) lastGroup = 1;
  return (
    <>
      <SimplePanel article={reportInfo.zendeskArticle} title={intl.formatMessage(reportInfo.title)}>
        <div className="info-block container-fluid">
          <RegistrationsPerReportFilters
            filters={filters}
            mainTaskTemplates={mainTaskTemplates}
            onFilterChange={updateFilters}
            rootGroup={reportInfo.rootGroup}
            reportKey={reportKey}
          />
        </div>
      </SimplePanel>

      <div className="row btn-row">
        <div className="col-md-6 pull-right content-right">
          {rows && rows.length > 0 && (
            <ExportToExcelDropdown
              exporting={exporting}
              createExcel={createExcelReport}
              intl={intl}
            />
          )}
        </div>
      </div>
      <RegistrationsPerReportTable
        data={rows}
        showDetails={showDetails}
        lastGroup={lastGroup}
        totals={totals}
      />
      {loading && (
        <div>
          <LoadingAnimation />
        </div>
      )}
      <ScrollArrow />
    </>
  );
};

RegistrationsPerReport.propTypes = {
  intl: intlShape.isRequired,
  externalParams: PropTypes.shape({ relationId: PropTypes.string }),
  reportType: PropTypes.oneOf([4, 5, 6, 7, 10]).isRequired,
  reportKey: PropTypes.string.isRequired,
};

RegistrationsPerReport.defaultProps = {
  externalParams: {},
};

export default injectIntl(RegistrationsPerReport);
